import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { Procedure, Ponderation, Tache, TacheRequest, User } from '../types';
import GanttChart from '../components/GanttChart';
import CalendarView from '../components/CalendarView';
import KanbanBoard from '../components/KanbanBoard';
import Pagination from '../components/Pagination';
import {
  ArrowLeftIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ListBulletIcon,
  ViewColumnsIcon,
  XMarkIcon,
  AdjustmentsHorizontalIcon,
  UserCircleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';

type ViewTab = 'kanban' | 'list' | 'gantt' | 'calendar' | 'ponderation';

/* ─────────────────────────────────────────────────────────
   Colour palette for tasks
───────────────────────────────────────────────────────── */
const PRESET_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#06B6D4', '#14B8A6',
];

/* ─────────────────────────────────────────────────────────
   Initials avatar helper
───────────────────────────────────────────────────────── */
function userInitials(prenom?: string | null, nom?: string | null) {
  return `${(prenom || '').charAt(0)}${(nom || '').charAt(0)}`.toUpperCase() || '?';
}

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500',
  'bg-pink-500', 'bg-cyan-500', 'bg-teal-500', 'bg-red-500',
];
function avatarColor(id?: string) {
  if (!id) return AVATAR_COLORS[0];
  const code = id.charCodeAt(id.length - 1) % AVATAR_COLORS.length;
  return AVATAR_COLORS[code];
}

/* ─────────────────────────────────────────────────────────
   KPI Card
───────────────────────────────────────────────────────── */
const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  extra?: React.ReactNode;
}> = ({ label, value, sub, icon: Icon, iconBg, iconColor, extra }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
    <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
      <Icon className={`h-6 w-6 ${iconColor}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <div className="flex items-end gap-1.5">{value}</div>
      {sub && <div className="mt-2">{sub}</div>}
      {extra && <div className="mt-2">{extra}</div>}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────
   Etat badge
───────────────────────────────────────────────────────── */
const EtatBadge: React.FC<{ etat: string; enRetard: boolean }> = ({ etat, enRetard }) => {
  if (enRetard)
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700">
        <ExclamationTriangleIcon className="w-3 h-3" /> En retard
      </span>
    );
  if (etat === 'terminee')
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700">
        <CheckCircleIcon className="w-3 h-3" /> Terminée
      </span>
    );
  if (etat === 'en_cours')
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700">
        <ClockIcon className="w-3 h-3" /> En cours
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
      En attente
    </span>
  );
};

/* ─────────────────────────────────────────────────────────
   Complexity badge
───────────────────────────────────────────────────────── */
const ComplexityBadge: React.FC<{ value: string }> = ({ value }) => {
  const map: Record<string, string> = {
    facile: 'bg-emerald-100 text-emerald-700',
    moyen: 'bg-amber-100 text-amber-700',
    difficile: 'bg-red-100 text-red-700',
  };
  const labels: Record<string, string> = { facile: 'Facile', moyen: 'Moyen', difficile: 'Difficile' };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${map[value] || 'bg-gray-100 text-gray-600'}`}>
      {labels[value] || value}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────
   Pondération helpers
───────────────────────────────────────────────────────── */
function extractLiItems(html: string | null | undefined): string[] {
  if (!html) return [];
  const div = document.createElement('div');
  div.innerHTML = html;
  return Array.from(div.querySelectorAll('li')).map(li => li.textContent?.trim() || '').filter(Boolean);
}

function isItemDone(statuses: { itemType: string; itemIndex: number; completed: boolean }[] | undefined, itemType: string, itemIndex: number): boolean {
  return statuses?.some(s => s.itemType === itemType && s.itemIndex === itemIndex && s.completed) ?? false;
}

/* ─────────────────────────────────────────────────────────
   ProcedureDetail
───────────────────────────────────────────────────────── */
const ProcedureDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isSuperAdmin, isResponsable, user, canModifyPonderation } = useAuth();
  const isUtilisateur = !isSuperAdmin() && !isResponsable();

  const [procedure, setProcedure] = useState<Procedure | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ViewTab>('kanban');
  const [tachePage, setTachePage] = useState(0);
  const TACHE_PAGE_SIZE = 10;
  const [showTacheModal, setShowTacheModal] = useState(false);
  const [editingTache, setEditingTache] = useState<Tache | null>(null);
  const [tacheForm, setTacheForm] = useState<Partial<TacheRequest>>({});
  const [structureUsers, setStructureUsers] = useState<User[]>([]);
  const [ponderation, setPonderation] = useState<Ponderation | null>(null);
  const [ponderationLoading, setPonderationLoading] = useState(false);
  const [togglingItem, setTogglingItem] = useState<string | null>(null);

  // Suppress unused-variable warning for isUtilisateur (kept for potential use)
  void isUtilisateur;

  useEffect(() => {
    const fetchProcedure = async () => {
      if (!id) return;
      try {
        const data = await apiService.getProcedure(id);
        setProcedure(data);
        if (data.structureId) {
          try {
            const users = await apiService.getUsersByStructure(data.structureId);
            setStructureUsers(users);
          } catch {
            // non-blocking
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProcedure();
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'ponderation' || !id || ponderation) return;
    setPonderationLoading(true);
    apiService.getPonderation(id)
      .then(setPonderation)
      .catch(console.error)
      .finally(() => setPonderationLoading(false));
  }, [activeTab, id, ponderation]);

  const handleToggleItem = async (phaseId: string, itemType: 'ACTIVITE' | 'LIVRABLE', itemIndex: number) => {
    if (!id || !canModifyPonderation()) return;
    const key = `${phaseId}-${itemType}-${itemIndex}`;
    if (togglingItem === key) return;
    setTogglingItem(key);
    try {
      const updated = await apiService.togglePhaseActivite(id, phaseId, itemType, itemIndex);
      setPonderation(updated);
      setProcedure(prev => prev ? { ...prev, ponderationScore: updated.totalScore } : prev);
    } catch (err) {
      console.error('Erreur toggle activité:', err);
    } finally {
      setTogglingItem(null);
    }
  };

  const canModify = () => {
    if (!procedure) return false;
    if (isSuperAdmin()) return true;
    if (isResponsable() && user?.structureId === procedure.structureId) return true;
    return false;
  };

  const canManageTaches = () => {
    if (!procedure) return false;
    if (isSuperAdmin()) return true;
    if (user?.structureId === procedure.structureId) return true;
    return false;
  };

  const handleTacheSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      if (editingTache) {
        await apiService.updateTache(editingTache.id, { ...tacheForm, procedureId: id } as TacheRequest);
      } else {
        await apiService.createTache({ ...tacheForm, procedureId: id } as TacheRequest);
      }
      const updatedProcedure = await apiService.getProcedure(id);
      setProcedure(updatedProcedure);
      setShowTacheModal(false);
      setEditingTache(null);
      setTacheForm({});
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleDeleteTache = async (tacheId: string) => {
    if (!id || !window.confirm('Supprimer cette tâche ?')) return;
    try {
      await apiService.deleteTache(tacheId);
      const updatedProcedure = await apiService.getProcedure(id);
      setProcedure(updatedProcedure);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleUpdateTacheEtat = async (tacheId: string, etat: string) => {
    if (!id) return;
    try {
      await apiService.updateTacheEtat(tacheId, etat);
      const updatedProcedure = await apiService.getProcedure(id);
      setProcedure(updatedProcedure);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleTacheClick = (tache: Tache) => {
    if (canManageTaches()) {
      setEditingTache(tache);
      setTacheForm({
        nom: tache.nom,
        description: tache.description || '',
        dateDebut: tache.dateDebut,
        dateFin: tache.dateFin,
        complexite: tache.complexite,
        etat: tache.etat,
        couleur: tache.couleur,
        livrables: tache.livrables || '',
        assignedUserId: tache.assignedUserId || null,
      });
      setShowTacheModal(true);
    }
  };

  const openNewTacheModal = () => {
    setEditingTache(null);
    setTacheForm({
      nom: '',
      description: '',
      dateDebut: '',
      dateFin: '',
      complexite: 'moyen',
      etat: 'en_attente',
      couleur: '#3B82F6',
      assignedUserId: null,
    });
    setShowTacheModal(true);
  };

  /* ── Loading state ── */
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-12 h-12 rounded-full border-[3px] border-primary-100 border-t-primary-600 animate-spin" />
        <p className="text-sm text-gray-400">Chargement de la procédure…</p>
      </div>
    );
  }

  /* ── Not found ── */
  if (!procedure) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-gray-400" />
        </div>
        <p className="text-gray-600 font-medium">Procédure introuvable</p>
        <Link to="/procedures" className="text-sm text-primary-600 hover:underline font-medium">
          ← Retour aux procédures
        </Link>
      </div>
    );
  }

  const tabs: { id: ViewTab; label: string; icon: React.ElementType }[] = [
    { id: 'kanban', label: 'Kanban', icon: ViewColumnsIcon },
    { id: 'list', label: 'Liste', icon: ListBulletIcon },
    { id: 'gantt', label: 'Gantt', icon: ChartBarIcon },
    { id: 'calendar', label: 'Calendrier', icon: CalendarDaysIcon },
    { id: 'ponderation', label: 'Pondération', icon: AdjustmentsHorizontalIcon },
  ];

  const phases = ponderation?.phases ?? [];

  /* ── Selected user preview in task modal ── */
  const selectedUser = structureUsers.find((u) => u.id === tacheForm.assignedUserId);

  return (
    <div className="space-y-6 pb-10">

      {/* ───── Header ───── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        {/* Breadcrumb + title */}
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate('/procedures')}
            className="mt-1 p-2 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0"
          >
            <ArrowLeftIcon className="h-4 w-4 text-gray-500" />
          </button>
          <div>
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1 text-xs text-gray-400 mb-1.5">
              <Link to="/procedures" className="hover:text-gray-600 transition-colors">Procédures</Link>
              <ChevronRightIcon className="h-3 w-3" />
              <span className="text-gray-600 font-medium">{procedure.nom}</span>
            </nav>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">{procedure.nom}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              <span>{procedure.structureNom}</span>
              <span className="mx-2 text-gray-300">•</span>
              <span>{procedure.annee}</span>
            </p>
          </div>
        </div>
        {/* Actions */}
        {canModify() && (
          <div className="flex items-center gap-2 flex-shrink-0 sm:mt-0">
            <Link
              to={`/procedures/${procedure.id}/edit`}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm font-medium
                rounded-xl text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors"
            >
              <PencilIcon className="h-4 w-4" />
              Modifier
            </Link>
          </div>
        )}
      </div>

      {/* ───── Bannière En exploitation ───── */}
      {procedure.statut === 'EN_EXPLOITATION' && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-2xl px-5 py-3.5">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold text-amber-800">Procédure passée en exploitation</p>
            <p className="text-xs text-amber-600 mt-0.5">Le score de pondération a atteint 100 % — cette procédure est officiellement en exploitation.</p>
          </div>
        </div>
      )}

      {/* ───── KPI Strip ───── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Progression */}
        <KpiCard
          label="Progression globale"
          icon={ChartBarIcon}
          iconBg="bg-primary-50"
          iconColor="text-primary-600"
          value={
            <>
              <span className="text-3xl font-bold text-gray-900">{procedure.ponderationScore}</span>
              <span className="text-base text-gray-400 mb-0.5">%</span>
            </>
          }
          sub={
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${procedure.ponderationScore}%` }}
              />
            </div>
          }
        />

        {/* Tâches */}
        <KpiCard
          label="Tâches terminées"
          icon={CheckCircleIconSolid}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          value={
            <>
              <span className="text-3xl font-bold text-gray-900">{procedure.tachesCompleted}</span>
              <span className="text-base text-gray-400 mb-0.5">/ {procedure.tachesTotal}</span>
            </>
          }
          sub={
            procedure.tachesTotal > 0 ? (
              <div className="flex gap-1">
                {Array.from({ length: Math.min(procedure.tachesTotal, 10) }).map((_, i) => (
                  <div
                    key={i}
                    className={`flex-1 h-1.5 rounded-full transition-colors ${
                      i < Math.round((procedure.tachesCompleted / procedure.tachesTotal) * Math.min(procedure.tachesTotal, 10))
                        ? 'bg-emerald-500'
                        : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            ) : null
          }
        />

        {/* Pondération détail */}
        <KpiCard
          label="Fiche pondération"
          icon={AdjustmentsHorizontalIcon}
          iconBg={procedure.statut === 'EN_EXPLOITATION' ? 'bg-amber-50' : 'bg-violet-50'}
          iconColor={procedure.statut === 'EN_EXPLOITATION' ? 'text-amber-600' : 'text-violet-600'}
          value={
            <>
              <span className={`text-3xl font-bold ${procedure.statut === 'EN_EXPLOITATION' ? 'text-amber-600' : 'text-violet-600'}`}>
                {procedure.tachesCompleted}
              </span>
              <span className="text-base text-gray-400 mb-0.5">/ {procedure.tachesTotal} tâches</span>
            </>
          }
          sub={
            procedure.tachesTotal > 0 ? (
              <div className={`w-full rounded-full h-1.5 ${procedure.statut === 'EN_EXPLOITATION' ? 'bg-amber-100' : 'bg-violet-100'}`}>
                <div
                  className={`h-1.5 rounded-full transition-all duration-500 ${procedure.statut === 'EN_EXPLOITATION' ? 'bg-amber-500' : 'bg-violet-500'}`}
                  style={{ width: `${(procedure.tachesCompleted / procedure.tachesTotal) * 100}%` }}
                />
              </div>
            ) : null
          }
          extra={
            <button
              onClick={() => setActiveTab('ponderation')}
              className={`text-xs font-medium hover:underline ${procedure.statut === 'EN_EXPLOITATION' ? 'text-amber-600 hover:text-amber-700' : 'text-violet-600 hover:text-violet-700'}`}
            >
              Voir la pondération →
            </button>
          }
        />
      </div>

      {/* ───── Tabs + Content ───── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Tab header */}
        <div className="px-5 py-3.5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          {/* Tab pills */}
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setTachePage(0); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-150 ${
                  activeTab === tab.id
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            ))}
          </div>
          {/* Add task CTA — not shown on pondération tab */}
          {canManageTaches() && activeTab !== 'ponderation' && (
            <button
              onClick={openNewTacheModal}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white
                bg-primary-600 hover:bg-primary-700 rounded-xl shadow-sm transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Ajouter une tâche</span>
              <span className="sm:hidden">Ajouter</span>
            </button>
          )}
        </div>

        {/* Tab content */}
        <div className="p-5">
          {activeTab === 'kanban' && (
            <KanbanBoard
              taches={procedure.taches || []}
              canUpdateStatus={true}
              onUpdateStatus={handleUpdateTacheEtat}
              onCardClick={canManageTaches() ? handleTacheClick : undefined}
            />
          )}

          {activeTab === 'list' && (() => {
            const taches = procedure.taches || [];
            const totalPages = Math.ceil(taches.length / TACHE_PAGE_SIZE);
            const paginated = taches.slice(tachePage * TACHE_PAGE_SIZE, (tachePage + 1) * TACHE_PAGE_SIZE);
            return (
              <div className="-mx-5 -mt-5">
                {!taches.length ? (
                  <div className="flex flex-col items-center justify-center py-14 gap-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center">
                      <ListBulletIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">Aucune tâche pour cette procédure</p>
                    {canManageTaches() && (
                      <button onClick={openNewTacheModal} className="text-sm text-primary-600 hover:underline font-medium">
                        Créer la première tâche
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="divide-y divide-gray-50">
                      {paginated.map((tache) => (
                        <div key={tache.id} className="px-5 py-4 hover:bg-gray-50 transition-colors group">
                          <div className="flex items-center justify-between gap-3">
                            {/* Left */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2.5 flex-wrap">
                                <span
                                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: tache.couleur }}
                                />
                                <span className="text-sm font-semibold text-gray-900 truncate">{tache.nom}</span>
                                <EtatBadge etat={tache.etat} enRetard={tache.enRetard} />
                                <ComplexityBadge value={tache.complexite} />
                              </div>
                              <div className="mt-1.5 flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <CalendarDaysIcon className="h-3 w-3" />
                                  {new Date(tache.dateDebut).toLocaleDateString('fr-FR')}
                                  {' → '}
                                  {new Date(tache.dateFin).toLocaleDateString('fr-FR')}
                                </span>
                                {tache.assignedUserPrenom && (
                                  <span className="flex items-center gap-1">
                                    <UserCircleIcon className="h-3 w-3" />
                                    {tache.assignedUserPrenom} {tache.assignedUserNom}
                                  </span>
                                )}
                              </div>
                              {tache.description && (
                                <p className="mt-1.5 text-xs text-gray-500 line-clamp-1">{tache.description}</p>
                              )}
                            </div>
                            {/* Right actions */}
                            {canManageTaches() && (
                              <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <select
                                  value={tache.etat}
                                  onChange={(e) => handleUpdateTacheEtat(tache.id, e.target.value)}
                                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                >
                                  <option value="en_attente">En attente</option>
                                  <option value="en_cours">En cours</option>
                                  <option value="terminee">Terminée</option>
                                </select>
                                <button
                                  onClick={() => handleTacheClick(tache)}
                                  className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                {canModify() && (
                                  <button
                                    onClick={() => handleDeleteTache(tache.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div className="px-5 py-3 border-t border-gray-100">
                        <Pagination
                          page={tachePage}
                          totalPages={totalPages}
                          totalElements={taches.length}
                          size={TACHE_PAGE_SIZE}
                          onPageChange={setTachePage}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {activeTab === 'gantt' && (
            <div className="-mx-5 -mt-5">
              <GanttChart taches={procedure.taches || []} onTacheClick={handleTacheClick} />
            </div>
          )}

          {activeTab === 'calendar' && (
            <div className="-mx-5 -mt-5">
              <CalendarView taches={procedure.taches || []} onTacheClick={handleTacheClick} />
            </div>
          )}

          {activeTab === 'ponderation' && (
            <div className="-mx-5 -mt-5">
              {/* Role banner */}
              {!canModifyPonderation() && (
                <div className="mx-0 px-5 py-2.5 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
                  <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span className="text-xs text-amber-700 font-medium">Lecture seule — cochez les activités et livrables pour calculer le score automatiquement</span>
                </div>
              )}

              {/* Loading state */}
              {ponderationLoading && (
                <div className="flex items-center justify-center py-16 gap-3">
                  <div className="w-8 h-8 rounded-full border-[3px] border-violet-100 border-t-violet-600 animate-spin" />
                  <span className="text-sm text-gray-400">Chargement de la pondération…</span>
                </div>
              )}

              {!ponderationLoading && (
              <>
              {/* Summary strip */}
              <div className="px-5 py-3 bg-violet-50 border-b border-violet-100 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-violet-600 uppercase tracking-wide">Score total</span>
                  <span className="text-2xl font-bold text-violet-700">{procedure.ponderationScore}</span>
                  <span className="text-sm text-violet-400">/ 100</span>
                </div>
                <div className="flex-1 max-w-xs">
                  <div className="w-full bg-violet-200 rounded-full h-2.5">
                    <div
                      className="bg-violet-600 h-2.5 rounded-full transition-all duration-700"
                      style={{ width: `${procedure.ponderationScore}%` }}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  {phases.filter(p => p.score > 0).length > 0 && (
                    <span className="text-xs text-violet-600 font-medium">
                      {phases.filter(p => p.score === p.poids).length} / {phases.length} phases complètes
                    </span>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Phase</th>
                      <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-16">Poids</th>
                      <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-16">Cumul</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Objectif</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Activités clés</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Livrables</th>
                      <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {phases.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-400">
                          <div className="flex flex-col items-center gap-2">
                            <AdjustmentsHorizontalIcon className="h-8 w-8 text-gray-300" />
                            <p>Aucune donnée de phase disponible</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      phases.map((phase) => {
                        const pct = phase.poids > 0 ? (phase.score / phase.poids) * 100 : 0;
                        const scoreColor =
                          pct === 0 ? 'text-gray-400' :
                          pct < 50 ? 'text-red-600' :
                          pct < 100 ? 'text-amber-600' :
                          'text-emerald-600';
                        const barColor =
                          pct === 0 ? 'bg-gray-200' :
                          pct < 50 ? 'bg-red-400' :
                          pct < 100 ? 'bg-amber-400' :
                          'bg-emerald-500';
                        const rowBg =
                          pct === 0 ? '' :
                          pct === 100 ? 'bg-emerald-50/40' :
                          '';

                        return (
                          <tr key={phase.code} className={`hover:bg-violet-50/30 transition-colors ${rowBg}`}>
                            {/* Phase */}
                            <td className="px-4 py-4 align-top min-w-[160px]">
                              <div className="flex items-start gap-2">
                                <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-100 text-violet-700 flex-shrink-0 mt-0.5">
                                  {phase.code}
                                </span>
                                <span className="text-xs font-semibold text-gray-800 leading-snug">{phase.nom}</span>
                              </div>
                            </td>
                            {/* Poids */}
                            <td className="px-3 py-4 text-center align-top">
                              <span className="text-sm font-bold text-gray-700">{phase.poids}%</span>
                            </td>
                            {/* Cumul */}
                            <td className="px-3 py-4 text-center align-top">
                              <span className="text-xs text-gray-500">{phase.cumul}%</span>
                            </td>
                            {/* Objectif */}
                            <td className="px-4 py-4 align-top max-w-[200px]">
                              <div
                                className="text-xs text-gray-600 leading-relaxed prose prose-xs max-w-none [&_p]:my-0.5 [&_ul]:my-0.5 [&_li]:my-0"
                                dangerouslySetInnerHTML={{ __html: phase.objectif || '' }}
                              />
                            </td>
                            {/* Activités clés */}
                            <td className="px-4 py-4 align-top min-w-[220px]">
                              <div className="space-y-2">
                                {extractLiItems(phase.activitesCles).map((item, i) => {
                                  const checked = isItemDone(phase.itemStatuses, 'ACTIVITE', i);
                                  const key = `${phase.id}-ACTIVITE-${i}`;
                                  return (
                                    <label
                                      key={i}
                                      className={`flex items-start gap-2 ${canModifyPonderation() ? 'cursor-pointer' : 'cursor-default'}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={!canModifyPonderation() || togglingItem !== null}
                                        onChange={() => handleToggleItem(phase.id, 'ACTIVITE', i)}
                                        className="mt-0.5 w-3.5 h-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500 flex-shrink-0"
                                      />
                                      <span className={`text-xs leading-relaxed ${checked ? 'line-through text-gray-400' : 'text-gray-700'} ${togglingItem === key ? 'opacity-50' : ''}`}>
                                        {item}
                                      </span>
                                    </label>
                                  );
                                })}
                                {extractLiItems(phase.activitesCles).length === 0 && (
                                  <span className="text-xs text-gray-400 italic">Aucune activité</span>
                                )}
                              </div>
                            </td>
                            {/* Livrables */}
                            <td className="px-4 py-4 align-top min-w-[180px]">
                              <div className="space-y-2">
                                {extractLiItems(phase.livrables).map((item, i) => {
                                  const checked = isItemDone(phase.itemStatuses, 'LIVRABLE', i);
                                  const key = `${phase.id}-LIVRABLE-${i}`;
                                  return (
                                    <label
                                      key={i}
                                      className={`flex items-start gap-2 ${canModifyPonderation() ? 'cursor-pointer' : 'cursor-default'}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={!canModifyPonderation() || togglingItem !== null}
                                        onChange={() => handleToggleItem(phase.id, 'LIVRABLE', i)}
                                        className="mt-0.5 w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
                                      />
                                      <span className={`text-xs leading-relaxed ${checked ? 'line-through text-gray-400' : 'text-gray-700'} ${togglingItem === key ? 'opacity-50' : ''}`}>
                                        {item}
                                      </span>
                                    </label>
                                  );
                                })}
                                {extractLiItems(phase.livrables).length === 0 && (
                                  <span className="text-xs text-gray-400 italic">Aucun livrable</span>
                                )}
                              </div>
                            </td>
                            {/* Score */}
                            <td className="px-3 py-4 align-top">
                              <div className="flex flex-col items-center gap-2">
                                <span className={`text-lg font-bold leading-none ${scoreColor}`}>
                                  {phase.score}
                                  <span className="text-[11px] font-normal text-gray-400">/{phase.poids}</span>
                                </span>
                                <div className="w-14 bg-gray-100 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full transition-all duration-500 ${barColor}`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                {pct === 100 && (
                                  <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                                    ✓ Complet
                                  </span>
                                )}
                                {pct > 0 && pct < 100 && (
                                  <span className="text-[10px] text-amber-600 font-medium">
                                    {Math.round(pct)}%
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {phases.length > 0 && (
                    <tfoot>
                      <tr className="bg-violet-50 border-t-2 border-violet-200">
                        <td className="px-4 py-3 text-sm font-bold text-gray-900">TOTAL</td>
                        <td className="px-3 py-3 text-center text-sm font-bold text-violet-700">100%</td>
                        <td className="px-3 py-3 text-center text-sm font-bold text-violet-700">100%</td>
                        <td colSpan={3} className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-violet-200 rounded-full h-2.5">
                              <div
                                className="bg-violet-600 h-2.5 rounded-full transition-all duration-700"
                                style={{ width: `${procedure.ponderationScore}%` }}
                              />
                            </div>
                            <span className="text-xs text-violet-600 font-semibold w-10">{procedure.ponderationScore}%</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className="text-lg font-bold text-violet-700">
                            {procedure.ponderationScore}
                            <span className="text-xs font-normal text-gray-400">/100</span>
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════
          Tache Modal
      ═══════════════════════════════════════════════ */}
      {showTacheModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-6 overflow-y-auto">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg my-auto overflow-hidden">
            {/* Modal header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-white">
                    {editingTache ? 'Modifier la tâche' : 'Nouvelle tâche'}
                  </h3>
                  <p className="text-xs text-primary-100 mt-0.5">{procedure.nom}</p>
                </div>
                <button
                  onClick={() => setShowTacheModal(false)}
                  className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                  <XMarkIcon className="h-4 w-4 text-white" />
                </button>
              </div>
            </div>

            {/* Modal body */}
            <form onSubmit={handleTacheSubmit} className="p-6 space-y-4">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nom de la tâche</label>
                <input
                  type="text"
                  required
                  value={tacheForm.nom || ''}
                  onChange={(e) => setTacheForm({ ...tacheForm, nom: e.target.value })}
                  placeholder="Ex: Analyse des besoins métiers"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2
                    focus:ring-primary-500 focus:border-primary-500 outline-none placeholder-gray-300 transition-shadow"
                />
              </div>

              {/* Dates + complexité (grid 2) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Date de début</label>
                  <input
                    type="date"
                    required
                    value={tacheForm.dateDebut || ''}
                    onChange={(e) => setTacheForm({ ...tacheForm, dateDebut: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2
                      focus:ring-primary-500 focus:border-primary-500 outline-none transition-shadow"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Date de fin</label>
                  <input
                    type="date"
                    required
                    value={tacheForm.dateFin || ''}
                    onChange={(e) => setTacheForm({ ...tacheForm, dateFin: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2
                      focus:ring-primary-500 focus:border-primary-500 outline-none transition-shadow"
                  />
                </div>
              </div>

              {/* Complexité + Statut (grid 2) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Complexité</label>
                  <select
                    value={tacheForm.complexite || 'moyen'}
                    onChange={(e) => setTacheForm({ ...tacheForm, complexite: e.target.value as any })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2
                      focus:ring-primary-500 focus:border-primary-500 outline-none transition-shadow bg-white"
                  >
                    <option value="facile">Facile</option>
                    <option value="moyen">Moyen</option>
                    <option value="difficile">Difficile</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Statut</label>
                  <select
                    value={tacheForm.etat || 'en_attente'}
                    onChange={(e) => setTacheForm({ ...tacheForm, etat: e.target.value as any })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2
                      focus:ring-primary-500 focus:border-primary-500 outline-none transition-shadow bg-white"
                  >
                    <option value="en_attente">En attente</option>
                    <option value="en_cours">En cours</option>
                    <option value="terminee">Terminée</option>
                  </select>
                </div>
              </div>

              {/* Color picker */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Couleur</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setTacheForm({ ...tacheForm, couleur: color })}
                      className="relative w-7 h-7 rounded-lg transition-transform hover:scale-110 focus:outline-none"
                      style={{ backgroundColor: color }}
                    >
                      {tacheForm.couleur === color && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                      )}
                    </button>
                  ))}
                  {/* Custom color */}
                  <label className="relative w-7 h-7 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 cursor-pointer flex items-center justify-center transition-colors" title="Couleur personnalisée">
                    <span className="text-gray-400 text-xs font-bold">+</span>
                    <input
                      type="color"
                      value={tacheForm.couleur || '#3B82F6'}
                      onChange={(e) => setTacheForm({ ...tacheForm, couleur: e.target.value })}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                  </label>
                  {/* Preview */}
                  <div
                    className="w-7 h-7 rounded-lg border border-gray-200"
                    style={{ backgroundColor: tacheForm.couleur || '#3B82F6' }}
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Description</label>
                <textarea
                  value={tacheForm.description || ''}
                  onChange={(e) => setTacheForm({ ...tacheForm, description: e.target.value })}
                  rows={2}
                  placeholder="Décrivez l'objectif de cette tâche…"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2
                    focus:ring-primary-500 focus:border-primary-500 outline-none resize-none placeholder-gray-300 transition-shadow"
                />
              </div>

              {/* Livrables */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Livrables</label>
                <textarea
                  value={tacheForm.livrables || ''}
                  onChange={(e) => setTacheForm({ ...tacheForm, livrables: e.target.value })}
                  rows={2}
                  placeholder="Documents, fichiers ou résultats attendus…"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2
                    focus:ring-primary-500 focus:border-primary-500 outline-none resize-none placeholder-gray-300 transition-shadow"
                />
              </div>

              {/* Assigné */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Assigné à
                  <span className="ml-1 font-normal text-gray-400">(optionnel)</span>
                </label>
                {/* Avatar preview */}
                {selectedUser && (
                  <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-primary-50 rounded-xl border border-primary-100">
                    <div className={`w-7 h-7 rounded-lg ${avatarColor(selectedUser.id)} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-white text-[10px] font-semibold">{userInitials(selectedUser.prenom, selectedUser.nom)}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-700">{selectedUser.prenom} {selectedUser.nom}</span>
                  </div>
                )}
                <select
                  value={tacheForm.assignedUserId || ''}
                  onChange={(e) => setTacheForm({ ...tacheForm, assignedUserId: e.target.value || null })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2
                    focus:ring-primary-500 focus:border-primary-500 outline-none bg-white transition-shadow"
                >
                  <option value="">— Aucun assigné —</option>
                  {structureUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.prenom} {u.nom}
                    </option>
                  ))}
                </select>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowTacheModal(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl shadow-sm transition-colors"
                >
                  {editingTache ? 'Mettre à jour' : 'Créer la tâche'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProcedureDetail;
